import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { LoadingButton } from '@mui/lab'
import { Button, MenuItem, Stack, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import {
  useCreatePubModelVersion,
  useGetPubModelVersions,
} from '@/service-library/hooks/pub-model-versions'
import { ProjectModelVersion } from '@/types/project-models'
import { PubModelVersion } from '@/types/pub-models'
import { showSuccessSnackbar, showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormSelect, PixyDocsForm } from '@/components/forms'

type PublishNewVersionDialogProps = {
  overlay: OverlayState
  pubModelId: string
  projectModelVersions: ProjectModelVersion[]
  existingPubModelVersions: PubModelVersion[]
  pubModelsQueryKey: QueryKey
}

type FormValues = {
  model_version_id: string
}

export default function PublishNewVersionDialog({
  overlay,
  pubModelId,
  projectModelVersions,
  existingPubModelVersions,
  pubModelsQueryKey,
}: PublishNewVersionDialogProps) {
  const { pubModelVersions: allPubModelVersions = [] } = useGetPubModelVersions(
    {
      filters: {
        pub_model_id: pubModelId,
        limit: '10000',
      },
    },
  )

  // Calculate the next version number
  const nextVersionArray = useMemo(() => {
    if (existingPubModelVersions.length === 0) {
      return [1, 0]
    }
    const highestVersion = existingPubModelVersions.reduce((max, v) => {
      const ver = v.version[0] * 100 + (v.version[1] || 0)
      return ver > max ? ver : max
    }, 0)
    const major = Math.floor(highestVersion / 100)
    // Increment major version
    return [major + 1, 0]
  }, [existingPubModelVersions])

  const methods = useForm<FormValues>({
    defaultValues: {
      model_version_id: '',
    },
  })

  const {
    formState: { isValid },
    reset,
  } = methods

  const { createPubModelVersion, isPending } = useCreatePubModelVersion({
    sideEffectQueryKeys: [pubModelsQueryKey],
    onSuccess: () => {
      showSuccessSnackbar('New version published successfully')
      overlay.close()
      reset()
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      showErrorSnackbar(`Failed to publish new version: ${errorMessage}`)
    },
  })

  // Create a set of already published version IDs
  const publishedVersionIds = new Set(
    allPubModelVersions.map((v) => v.model_version_id),
  )

  // Include all completed versions, sorted by version descending
  const allCompletedVersions = projectModelVersions
    .filter((v) => v.training_status === 'completed')
    .sort((a, b) => b.version - a.version)

  // Check if there are any unpublished versions
  const hasAvailableVersions = allCompletedVersions.some(
    (v) => !publishedVersionIds.has(v.id),
  )

  const handleSubmit = (data: FormValues) => {
    createPubModelVersion({
      id: generateUuid(),
      pub_model_id: pubModelId,
      model_version_id: data.model_version_id,
      version: nextVersionArray,
    })
  }

  useEffect(() => {
    if (overlay.isOpen) {
      reset({ model_version_id: '' })
    }
  }, [overlay.isOpen, reset])

  return (
    <Dialog maxWidth="xs" fullWidth title="Publish New Version" {...overlay}>
      <DialogContent>
        <PixyDocsForm
          id="publish-version-form"
          methods={methods}
          onSubmit={handleSubmit}
        >
          <Stack spacing={2}>
            {allCompletedVersions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unpublished trained versions available. All completed model
                versions have already been published.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Select a trained model version to publish as version{' '}
                  <strong>
                    v{nextVersionArray[0]}.{nextVersionArray[1]}
                  </strong>
                  . Already published versions are disabled.
                </Typography>
                <FormSelect
                  name="model_version_id"
                  label="Model Version"
                  required
                  variant="filled"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                >
                  {allCompletedVersions.map((version) => {
                    const isPublished = publishedVersionIds.has(version.id)
                    return (
                      <MenuItem
                        key={version.id}
                        value={version.id}
                        disabled={isPublished}
                      >
                        Version v{version.version}.0 - Trained on{' '}
                        {version.training_started_at
                          ? new Date(
                              version.training_started_at,
                            ).toLocaleDateString()
                          : 'Unknown'}
                        {isPublished && ' (Already Published)'}
                      </MenuItem>
                    )
                  })}
                </FormSelect>
              </>
            )}
          </Stack>
        </PixyDocsForm>
      </DialogContent>
      <DialogFooter>
        <Button onClick={overlay.close} variant="text">
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          form="publish-version-form"
          variant="contained"
          loading={isPending}
          disabled={!isValid || !hasAvailableVersions}
        >
          Publish
        </LoadingButton>
      </DialogFooter>
    </Dialog>
  )
}
